import { ConfirmationDialog, ErpDataTableV2 } from '@/components/custom/erp';
import type { NguonDonInput, NguonDon } from '@/types/schemas';
import { getDataTableColumns } from './cols-definition';
import { ActionBar, FormDialog } from './components';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function DanhMucNguonDon() {
  const {
    data: orderSources,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    isLoading,
    searchData
  } = useCRUD<NguonDon, NguonDonInput>({
    endpoint: QUERY_KEYS.DANH_MUC_NGUON_DON,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<NguonDon>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : orderSources || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const columns = getDataTableColumns();

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: columns
    }
  ];

  const handleAddOrderSource = async (data: NguonDonInput) => {
    await addItem(data);
    await refreshData();
  };

  const handleUpdateOrderSource = async (data: NguonDonInput) => {
    if (selectedObj) {
      await updateItem(selectedObj.uuid, data);
      await refreshData();
    }
  };

  const handleSubmit = async (data: NguonDonInput) => {
    try {
      if (formMode === 'add') {
        await handleAddOrderSource(data);
      } else if (formMode === 'edit' && selectedObj) {
        await handleUpdateOrderSource(data);
      }
      handleCloseForm();
      clearSelection();
    } catch (e) {
      return;
    }
  };

  return (
    <div className='flex h-screen flex-col lg:overflow-hidden'>
      <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
        <ActionBar
          onAddClick={handleAddClick}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          onCopyClick={handleCopyClick}
          onViewClick={handleViewClick}
          onRefreshClick={async () => {
            await refreshData();
            if (selectedObj?.uuid) {
              await fetchDetail(selectedObj.uuid);
            }
          }}
        />

        <ErpDataTableV2
          tables={tables}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowIndex || undefined}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
        />
      </div>

      {showForm && (
        <FormDialog
          mode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onCopyButtonClick={handleCopyClick}
          onWatchButtonClick={handleViewClick}
          onDeleteButtonClick={handleDeleteClick}
          onSubmit={handleSubmit}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
        />
      )}

      {showDelete && (
        <ConfirmationDialog
          onClose={handleCloseDelete}
          onConfirm={async () => {
            if (selectedObj) {
              await deleteItem(selectedObj.uuid);
              await refreshData();
              handleCloseDelete();
              clearSelection();
            }
          }}
        />
      )}
    </div>
  );
}
